import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../components/ui/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import Icon from '../components/AppIcon';
import { sendContactFormEmail } from '../utils/emailService';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const location = useLocation();

  // Scroll to contact form when hash is present
  useEffect(() => {
    if (location.hash === '#contact-form') {
      setTimeout(() => {
        const element = document.getElementById('contact-form');
        if (element) {
          const headerHeight = 100; // Approximate height of header
          const elementPosition = element.offsetTop - headerHeight;
          window.scrollTo({
            top: elementPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [location.hash]);

  // Enhanced SEO Data
  const seoData = {
    title: 'Contact Us - OctalChip | Get in Touch for Web & Mobile Development',
    description: 'Contact OctalChip for web development, mobile app development, AI solutions, and digital transformation services. Get a free consultation and quote for your project.',
    keywords: 'contact OctalChip, web development contact, mobile app development contact, software development contact, development agency contact, tech consulting contact, IT services contact, project consultation, free quote, development services, custom software development, web applications, mobile applications, AI development, cloud services, DevOps, UI/UX design, enterprise software, SaaS development, e-commerce development, API development, database design, frontend development, backend development, full-stack development, project inquiry, development quote, technology consultation',
    canonical: 'https://www.octalchip.com/contact',
    tags: ['contact OctalChip', 'project consultation', 'free quote', 'development services'],
    additionalMeta: [
      { name: 'theme-color', content: '#00d690' },
      { name: 'mobile-web-app-capable', content: 'yes' }
    ],
    breadcrumbs: [
      { name: 'Home', url: 'https://www.octalchip.com/' },
      { name: 'Contact Us', url: 'https://www.octalchip.com/contact' }
    ],

    // GEO-specific data
    geoData: {
      region: "IN-GJ",
      placename: "Gujarat, India",
      latitude: "23.0225",
      longitude: "72.5714"
    },
    localBusinessData: true,
    serviceArea: [
      { "@type": "Country", "name": "India" },
      { "@type": "Country", "name": "Germany" },
      { "@type": "Country", "name": "Italy" },
      { "@type": "Country", "name": "Dubai" },
      { "@type": "Country", "name": "United Kingdom" },
      { "@type": "Country", "name": "Netherlands" }
    ],

    // AEO-specific data
    faqData: [
      {
        question: 'How can I contact OctalChip?',
        answer: 'You can contact us via email at business@octalchip.com, phone at +91-7574082582, WhatsApp, or by filling out our contact form on this page.'
      },
      {
        question: 'What are your business hours?',
        answer: 'Our business hours are Monday to Friday, 9 AM to 6 PM IST. However, we also provide 24/7 support for urgent projects.'
      },
      {
        question: 'Do you offer free consultations?',
        answer: 'Yes, we offer free 30-minute consultations to discuss your project requirements and provide initial guidance.'
      },
      {
        question: 'How quickly do you respond to inquiries?',
        answer: 'We respond to email inquiries within 24 hours, WhatsApp messages within 2 hours, and phone calls immediately during business hours.'
      },
      {
        question: 'What information should I include in my project inquiry?',
        answer: 'Please include your project requirements, timeline, budget range, and any specific technologies or features you need. The more details you provide, the better we can assist you.'
      },
      {
        question: 'Do you work with international clients?',
        answer: 'Yes, we work with clients globally. We have experience working across different time zones and can accommodate various communication preferences.'
      }
    ],

    // Voice search optimization
    voiceSearchKeywords: [
      "contact OctalChip",
      "web development contact",
      "mobile app development contact",
      "software development consultation",
      "development company contact",
      "tech consulting contact",
      "project inquiry",
      "free consultation"
    ],
    conversationalQueries: [
      "How can I contact OctalChip?",
      "What is OctalChip's phone number?",
      "How to get a quote from OctalChip?",
      "What are OctalChip's business hours?",
      "How to schedule a consultation with OctalChip?"
    ],

    // Featured snippet optimization
    featuredSnippetContent: "Contact OctalChip for web development, mobile app development, and AI solutions. Email: business@octalchip.com, Phone: +91-7574082582. Free 30-minute consultations available.",
    
    // List data for featured snippets
    listData: {
      name: "OctalChip Contact Methods",
      description: "Different ways to contact OctalChip for your project needs",
      items: [
        {
          name: "Email Contact",
          description: "Send detailed project requirements via email",
          url: "mailto:business@octalchip.com"
        },
        {
          name: "Phone Call",
          description: "Call for immediate assistance during business hours",
          url: "tel:+917574082582"
        },
        {
          name: "WhatsApp Chat",
          description: "Quick responses via WhatsApp messaging",
          url: "https://wa.me/917574082582"
        },
        {
          name: "Contact Form",
          description: "Fill out our detailed contact form online",
          url: "https://www.octalchip.com/contact"
        },
        {
          name: "Schedule Meeting",
          description: "Book a 30-minute free consultation",
          url: "https://cal.com/octalchip/30min"
        }
      ]
    },
    structuredData: {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contact OctalChip",
      "description": "Contact OctalChip for web development, mobile app development, AI solutions, and digital transformation services.",
      "url": "https://www.octalchip.com/contact",
      "mainEntity": {
        "@type": "Organization",
        "name": "OctalChip",
        "url": "https://www.octalchip.com",
        "logo": "https://www.octalchip.com/favicon.svg",
        "description": "Leading web and mobile app development company offering AI-powered solutions, custom software development, cloud services, and digital transformation.",
        "contactPoint": [
          {
            "@type": "ContactPoint",
            "contactType": "customer service",
            "email": "business@octalchip.com",
            "telephone": "+91-7574082582",
            "availableLanguage": "English",
            "hoursAvailable": {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              "opens": "09:00",
              "closes": "18:00"
            }
          },
          {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "business@octalchip.com",
            "availableLanguage": "English"
          },
          {
            "@type": "ContactPoint",
            "contactType": "technical support",
            "email": "business@octalchip.com",
            "availableLanguage": "English"
          }
        ],
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "404, Opera point, kothariya",
          "addressLocality": "Gujarat",
          "addressCountry": "IN",
          "postalCode": "360022"
        },
        "sameAs": [
          "https://linkedin.com/company/octalchip",
          "https://twitter.com/octalchip",
          "https://github.com/octalchip",
          "https://www.facebook.com/octalchip",
          "https://www.instagram.com/octalchip/"
        ]
      }
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      await sendContactFormEmail(formData);
      setSubmitMessage('Thank you! Your message has been sent successfully. We\'ll get back to you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      setSubmitMessage('Sorry, there was an error sending your message. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: 'MapPin',
      title: 'Head Office',
      details: [
        'Octalchip',
        '404, Opera point, kothariya,',
        'gujarat, india, 360022'
      ],
      link: 'https://maps.google.com/?q=404+Opera+point+kothariya+gujarat+india+360022'
    },
    {
      icon: 'Mail',
      title: 'Email',
      details: ['business@octalchip.com'],
      link: 'mailto:business@octalchip.com'
    },
    {
      icon: 'Phone',
      title: 'Phone',
      details: ['+91 7574082582'],
      link: 'tel:+917574082582'
    },
    {
      icon: 'Clock',
      title: 'Working Hours',
      details: ['Mon - Fri, 9AM - 6PM'],
      link: null
    }
  ];

  const socialLinks = [
    { name: 'Facebook', icon: 'Facebook', url: 'https://www.facebook.com/octalchip' },
    { name: 'Instagram', icon: 'Instagram', url: 'https://www.instagram.com/octalchip/' },
    { name: 'LinkedIn', icon: 'Linkedin', url: 'https://www.linkedin.com/company/octalchip' },
    { name: 'Twitter', icon: 'Twitter', url: 'https://x.com/octalchip' }
  ];

  return (
    <div className="min-h-screen bg-[#f4faf8]">
      <Header />
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        ogImage={seoData.ogImage}
        tags={seoData.tags}
        additionalMeta={seoData.additionalMeta}
        breadcrumbs={seoData.breadcrumbs}
        faqData={seoData.faqData}
        structuredData={seoData.structuredData}
        geoData={seoData.geoData}
        localBusinessData={seoData.localBusinessData}
        serviceArea={seoData.serviceArea}
        voiceSearchKeywords={seoData.voiceSearchKeywords}
        conversationalQueries={seoData.conversationalQueries}
        featuredSnippetContent={seoData.featuredSnippetContent}
        listData={seoData.listData}
      />
      
      {/* Hero Section */}
      <section className="bg-white py-20">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl font-bold text-gray-800 mb-6">
              Get In Touch
            </h1>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-8"></div>
            <p className="text-xl text-gray-600 leading-relaxed">
              Ready to start your next project? We'd love to hear from you. 
              Let's discuss how we can help bring your ideas to life.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Contact Information</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto"></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactInfo.map((info, index) => (
              <div key={index} className="text-center p-6 bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name={info.icon} size={32} className="text-[#00d690]" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{info.title}</h3>
                {info.link ? (
                  <a 
                    href={info.link} 
                    target={info.link.startsWith('http') ? '_blank' : '_self'}
                    rel={info.link.startsWith('http') ? 'noopener noreferrer' : ''}
                    className="text-gray-600 hover:text-[#00d690] transition-colors"
                  >
                    {info.details.map((detail, idx) => (
                      <p key={idx} className="mb-1">{detail}</p>
                    ))}
                  </a>
                ) : (
                  info.details.map((detail, idx) => (
                    <p key={idx} className="text-gray-600 mb-1">{detail}</p>
                  ))
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">Send Us a Message</h2>
              <div className="w-24 h-1 bg-[#00d690] mx-auto"></div>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00d690]/20 focus:border-[#00d690]"
                        placeholder="Enter your full name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00d690]/20 focus:border-[#00d690]"
                        placeholder="Enter your email"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00d690]/20 focus:border-[#00d690]"
                        placeholder="Enter your phone number"
                      />
                    </div>
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                        Subject *
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00d690]/20 focus:border-[#00d690]"
                        placeholder="What is this about?"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00d690]/20 focus:border-[#00d690]"
                      placeholder="Tell us about your project or inquiry..."
                    ></textarea>
                  </div>
                  
                  {submitMessage && (
                    <div className={`p-4 rounded-lg ${
                      submitMessage.includes('Thank you') 
                        ? 'bg-green-50 text-green-800 border border-green-200' 
                        : 'bg-red-50 text-red-800 border border-red-200'
                    }`}>
                      {submitMessage}
                    </div>
                  )}
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#00d690] hover:bg-[#00b876] text-white py-3 px-6 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>
              
              {/* Quick Contact */}
              <div className="space-y-8">
                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-6">Quick Contact</h3>
                  <div className="space-y-4">
                    <a
                      href="https://wa.me/917574082582?text=Hi! I'm interested in your web development services. Can you help me get started?"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                    >
                      <Icon name="MessageCircle" size={24} className="text-green-600" />
                      <div>
                        <div className="font-semibold text-green-800">WhatsApp Chat</div>
                        <div className="text-sm text-green-600">Get instant response</div>
                      </div>
                    </a>
                    
                    <a
                      href="https://cal.com/octalchip/30min"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      <Icon name="Calendar" size={24} className="text-blue-600" />
                      <div>
                        <div className="font-semibold text-blue-800">Book a Meeting</div>
                        <div className="text-sm text-blue-600">30-minute consultation</div>
                      </div>
                    </a>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-6">Follow Us</h3>
                  <div className="flex space-x-4">
                    {socialLinks.map((social, index) => (
                      <a
                        key={index}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 bg-[#00d690]/10 rounded-full flex items-center justify-center hover:bg-[#00d690] hover:text-white transition-colors"
                      >
                        <Icon name={social.icon} size={24} />
                      </a>
                    ))}
                  </div>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="text-lg font-bold text-gray-800 mb-4">Response Time</h4>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex justify-between">
                      <span>Email:</span>
                      <span className="font-medium">Within 24 hours</span>
                    </div>
                    <div className="flex justify-between">
                      <span>WhatsApp:</span>
                      <span className="font-medium">Within 2 hours</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Phone:</span>
                      <span className="font-medium">Immediate</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactUs; 